import type { BlueprintType } from '@/api-types';
import clsx from 'clsx';
import { Markdown } from './messages';
import { Blueprint } from './blueprint';

export function StreamingBlueprint({
	blueprint,
	blueprintMarkdown,
	isGeneratingBlueprint,
	className,
	...props
}: React.ComponentProps<'div'> & {
	blueprint: BlueprintType | null;
	blueprintMarkdown: string;
	isGeneratingBlueprint: boolean;
}) {
	// If we have a completed blueprint, show the structured view
	if (blueprint && !isGeneratingBlueprint) {
		return <Blueprint blueprint={blueprint} className={className} {...props} />;
	}

	// If we're generating and have streaming content, show the markdown
	if (isGeneratingBlueprint && blueprintMarkdown) {
		return (
			<div className={clsx('w-full flex flex-col', className)} {...props}>
				<div className="bg-accent p-6 rounded-t-xl flex items-center bg-graph-paper">
					<div className="flex flex-col gap-1">
						<div className="uppercase text-xs tracking-wider text-text-on-brand/90">
							Blueprint
						</div>
						<div className="text-2xl font-medium text-text-on-brand">
							Generating...
						</div>
					</div>
				</div>
				<div className="flex flex-col px-6 py-4 bg-bg-2 rounded-b-xl">
					<div className="relative">
						<Markdown className="text-text-50">
							{blueprintMarkdown}
						</Markdown>
						{/* Typing cursor animation */}
						<span className="inline-block w-2 h-5 bg-blue-400 animate-pulse ml-1" />
					</div>
				</div>
			</div>
		);
	}

	// If we're generating but no content yet, show loading state
	if (isGeneratingBlueprint) {
		return (
			<div className={clsx('w-full flex flex-col', className)} {...props}>
				<div className="bg-accent p-6 rounded-t-xl flex items-center bg-graph-paper">
					<div className="flex flex-col gap-1">
						<div className="uppercase text-xs tracking-wider text-text-on-brand/90">
							Blueprint
						</div>
						<div className="text-2xl font-medium text-text-on-brand">
							Generating...
						</div>
					</div>
				</div>
				<div className="flex flex-col px-6 py-4 bg-bg-2 rounded-b-xl">
					<div className="flex items-center gap-2 text-text-50/60">
						<div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
						<span className="text-sm">Analyzing your request...</span>
					</div>
				</div>
			</div>
		);
	}

	// No blueprint and not generating
	return null;
}
